import { useEffect, useMemo, useState } from 'react';
import { sortBy } from 'lodash-es';
import { stringify } from 'qs';
import * as Sentry from '@sentry/react';
import { useEventSource } from 'remix-utils/sse/react';
import { TasksEventStreamMessage } from 'admin/data-tasks';
import { notification } from 'feature-notifications';
import { logger } from 'util-logger';
import { updateTasks, useTasks } from './store';

const eventSourceInit = {
  withCredentials: true,
};

export function useTaskPolling() {
  const { tasks, incompleteTasks } = useTasks();
  const taskIdsPerMarket = useMemo(
    () =>
      sortBy(
        [...tasks].map(([marketId, tasks]) => [marketId, tasks.map((task) => task.id)]) as [
          string,
          string[],
        ][],
        [0],
      ),
    [tasks],
  );

  const [enabled, setEnabled] = useState(incompleteTasks.length > 0);

  const event = useEventSource(`/sse/tasks?${stringify({ tasks: taskIdsPerMarket })}`, {
    enabled,
    init: eventSourceInit,
  });

  useEffect(() => {
    setEnabled(incompleteTasks.length > 0);
  }, [incompleteTasks.length]);

  useEffect(() => {
    try {
      if (event) {
        const tasksResult = JSON.parse(event);
        const tasksResultParsed = TasksEventStreamMessage.parse(tasksResult);
        if ('errors' in tasksResultParsed) {
          logger.error(tasksResultParsed.errors, 'Tasks included errors');
          Sentry.captureException(
            new Error(`Tasks included errors: ${tasksResultParsed.errors.join(', ')}`),
          );
          notification.error({
            title: 'Tasks included errors',
            message: tasksResultParsed.errors.join(', '),
          });
          setEnabled(false);
        }

        if ('tasks' in tasksResultParsed) {
          const { tasks } = tasksResultParsed;
          for (const [marketId, t] of Object.entries(tasks)) {
            updateTasks(marketId, t);
          }
        }
      }
    } catch (error) {
      logger.error(error, 'Failed to parse tasks event stream');
      Sentry.captureException(error);
      notification.error({
        title: 'Failed to parse tasks event stream',
        message: (error as Error).message,
      });
      setEnabled(false);
    }
  }, [event]);
}

export function Poller() {
  useTaskPolling();
  return null;
}
